import type {
  LevelLeaderboardEntry,
  MyLevelRank,
  MyWorldRank,
  RunSubmission,
  ServiceResult,
  WorldLeaderboardEntry,
} from "./types";
import { OnlineFeaturesStorage } from "./OnlineFeaturesStorage";
import type { Platform } from "@/platform/Platform";
import { supabaseLeaderboardService } from "./SupabaseLeaderboardService";
import { offlineLeaderboardService } from "./OfflineLeaderboardService";

/** What happened to one `submitRun()` call - never a thrown error. `"disabled"`: Online Features are off, so nothing was sent (a completed run is still kept locally - see `OfflineLeaderboardService.submitRun()`). */
export type SubmitRunOutcome = "sent" | "queued" | "disabled";

/**
 * The one interface every Scene talks to for anything leaderboard/run
 * related - no Scene ever imports Supabase, `fetch`, or `AuthService`
 * directly. Three implementations share it:
 *  - `SupabaseLeaderboardService` - the real online backend;
 *  - `OfflineLeaderboardService` - Online Features disabled (or not yet
 *    decided): every read resolves to `{ state: "offline" }` and no
 *    submission is ever sent or queued, with zero network calls;
 *  - `MockLeaderboardService` - canned/configurable results for tests and
 *    UI development, also with zero network calls.
 *
 * Every method resolves, never rejects - failures are represented in
 * `ServiceResult`/`SubmitRunOutcome`, so a server or network problem can
 * never throw out of a Scene's click handler.
 */
export interface LeaderboardService {
  /** Cheap, synchronous, no network - just "would calling a read/write method here even attempt one?". Drives the UI's offline-state messaging before a request is ever made. */
  isAvailable(): boolean;

  /**
   * PR34: every Campaign board is per Mobile/Desktop `platform` - a level
   * board and "my rank" on it read the same platform; the world board sums
   * one platform's level bests only (never mixing platforms across levels).
   * Legacy `unknown` rows are never returned.
   */
  getLevelLeaderboard(levelId: string, platform: Platform): Promise<ServiceResult<LevelLeaderboardEntry[]>>;
  getMyLevelRank(levelId: string, platform: Platform): Promise<ServiceResult<MyLevelRank>>;
  getWorldLeaderboard(worldId: string, platform: Platform): Promise<ServiceResult<WorldLeaderboardEntry[]>>;
  getMyWorldRank(worldId: string, platform: Platform): Promise<ServiceResult<MyWorldRank>>;

  getMyDisplayName(): Promise<ServiceResult<string | null>>;
  setDisplayName(name: string): Promise<ServiceResult<string>>;

  /** Always resolves. On failure, queues the run locally (`RunQueue`) and resolves `"queued"` rather than rejecting - a submission failure must never surface as an error to the player. */
  submitRun(run: RunSubmission): Promise<SubmitRunOutcome>;

  /** Best-effort retry of anything already queued. Call at startup and on the browser's `online` event. */
  flushQueue(): Promise<void>;
}

/**
 * Picks the live implementation on every call (not cached across calls),
 * so a player enabling Online Features mid-session from the Leaderboard
 * screen takes effect immediately, without any reactive plumbing - the
 * next call just resolves to the other service.
 */
export function getLeaderboardService(): LeaderboardService {
  return OnlineFeaturesStorage.isEnabled() ? supabaseLeaderboardService : offlineLeaderboardService;
}
